import { useTranslation } from "react-i18next";
import { Globe } from "lucide-react";
import { chooseLanguage } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface Language {
  code: string;
  name: string;
  flag: string;
}

const languages: Language[] = [
  { code: "en", name: "English", flag: "🇺🇸" },
  { code: "es", name: "Español", flag: "🇲🇽" },
];

interface LanguageSelectorProps {
  /** Variant for different use cases */
  variant?: "dropdown" | "compact";
  /** Additional CSS classes */
  className?: string;
}

/**
 * Language selector component for switching between supported languages.
 * Persists the choice (chooseLanguage); a choice beats the tenant language.
 */
export function LanguageSelector({
  variant = "dropdown",
  className,
}: LanguageSelectorProps) {
  const { i18n, t } = useTranslation();
  const currentLanguage = languages.find((l) => l.code === i18n.language) || languages[0];

  const handleLanguageChange = (langCode: string) => {
    chooseLanguage(langCode);
  };

  // Compact variant - just a button that cycles through languages
  if (variant === "compact") {
    const nextLanguage = languages.find((l) => l.code !== i18n.language) || languages[0];
    return (
      <Button
        variant="ghost"
        size="sm"
        onClick={() => handleLanguageChange(nextLanguage.code)}
        className={className}
        aria-label={t("common.language")}
        data-testid="language-toggle"
      >
        <span className="mr-1">{currentLanguage.flag}</span>
        <span className="text-xs uppercase">{currentLanguage.code}</span>
      </Button>
    );
  }

  // Dropdown variant - full select menu
  return (
    <Select value={i18n.language} onValueChange={handleLanguageChange}>
      <SelectTrigger
        className={className}
        aria-label={t("common.language")}
        data-testid="language-select"
      >
        <Globe className="h-4 w-4 mr-2" aria-hidden="true" />
        <SelectValue>
          <span className="flex items-center gap-2">
            <span aria-hidden="true">{currentLanguage.flag}</span>
            <span>{currentLanguage.name}</span>
          </span>
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {languages.map((lang) => (
          <SelectItem key={lang.code} value={lang.code}>
            <span className="flex items-center gap-2">
              <span aria-hidden="true">{lang.flag}</span>
              <span>{lang.name}</span>
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export default LanguageSelector;
